import React, { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { IoIosArrowBack } from "react-icons/io";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm, UseFormReturn } from "react-hook-form";
import { jwtDecode } from 'jwt-decode';
import { PrimaryButton } from 'web/components/PrimaryButton';
import { Field } from 'web/components/Field';
import { SignUpParams, useGetInvitation, useSignUp } from 'web/services/api/queries';
import { Card } from 'web/components/Card';
import { StringParam, useQueryParam } from 'use-query-params';
import GoogleLoginButton from 'web/components/GoogleLoginButton';
import ErrorBanner from 'web/components/ErrorBanner';

interface SignupFormParams extends SignUpParams {
    confirmPassword: string;
}

interface JwtTokenPayload {
    Email?: string;
    EmailVerified?: boolean;
    FirstName?: string;
    LastName?: string;
}

const Signup = () => {
    const [invitationCode] = useQueryParam('inv', StringParam);
    const { data: invitation, isLoading: isInvitationLoading, error: invitationError } = useGetInvitation(invitationCode);

    if (invitationError) {
        return (
            <Card>
                <Card.Header title='Invitation Error' />
                <p className="text-[#1C1C1C] font-semibold">Invitation not found. Please contact support.</p>
            </Card>
        );
    }

    if (isInvitationLoading) {
        return null;
    }

    return (
        <SignupForm invitation={invitation} />
    );
};

const SignupForm = ({invitation}: {invitation?: { id: string; email: string; organizationName: string; organizationId: string; } | null}) => {
    const { mutate: signUp, data: isSignUp, isLoading: isSignUpLoading, error: signUpError } = useSignUp();
    const [ token ] = useQueryParam('token', StringParam);

    const tokenData = useMemo(() => {
        if (token) {
            return jwtDecode<JwtTokenPayload>(token);
        }
        return undefined;
    }, [token]);

    const form = useForm<SignupFormParams>({
        mode: "onChange",
        defaultValues: {
            email: invitation?.email,
            firstName: tokenData?.FirstName,
            lastName: tokenData?.LastName,
            organizationName: invitation?.organizationName,
            token: token ?? undefined,
        },
    });
    const { handleSubmit, formState: {isValid, isSubmitted}, trigger, setValue } = form;
    const [step, setStep] = useState<1 | 2>(token ? 2 : 1);

    useEffect(() => {
        if (tokenData?.FirstName) {
            setValue('firstName', tokenData.FirstName);
        }
        if (tokenData?.LastName) {
            setValue('lastName', tokenData.LastName);
        }
    }, [tokenData, setValue]);

    const onSubmit = async (data: SignupFormParams) => {
        await signUp({
            firstName: data.firstName?.trim(),
            lastName: data.lastName?.trim(),
            organizationName: data.organizationName?.trim(),
            email: data.email?.trim(),
            password: data.password?.trim(),
            promoCode: data.promoCode?.trim(),
            referralCode: data.referralCode?.trim(),
            recaptchaToken: data.recaptchaToken?.trim(),
            invitationCode: invitation?.id ?? undefined,
            token: token ?? undefined,
        }, {
            onSuccess: () => {
                if (tokenData?.EmailVerified) {
                    window.location.href = '/';
                }
            }
        });
    };

    if (isSubmitted && isSignUp) {
        return <SignupSuccess />
    }

    return (
        <Card>
            <form onSubmit={handleSubmit(onSubmit)} className=''>
                {step === 1 && (
                    <SignupStepOne
                        form={form}
                        invitation={invitation}
                        next={() => isValid ? setStep(2) : trigger(['email', 'password', 'confirmPassword'])} />
                )}

                {step === 2 && (
                    <SignupStepTwo
                        form={form}
                        invitation={invitation}
                        isLoading={isSignUpLoading}
                        signUpError={signUpError}
                        token={token ?? null}
                        back={() => setStep(1)} />
                )}
            </form>
            <Card.Tail>
                <p className='text-sm text-[#595959]'>By signing up and using the services, you confirm that you have accepted our <a href="/terms-of-service" className='text-[#26818E] underline'>Terms and Conditions</a> and have read our <a href="/privacy-policy" className='text-[#26818E] underline'>Privacy Policy</a>.</p>
            </Card.Tail>
        </Card>
    );
};


const SignupStepOne = ({form, next, invitation}: {
    form: UseFormReturn<SignupFormParams>;
    next: () => void;
    invitation?: { email: string; organizationName: string; organizationId: string; } | null;
}) => {
    const { register, formState: {dirtyFields, errors}, setFocus } = form;
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setFocus('email');
    }, [setFocus]);

    return (
        <>
            <Card.Header title='Registration' subtitle='Account Information' />
            {error && <ErrorBanner message={error} />}
            <div className="flex flex-col gap-5">
                <Field
                    label="Email"
                    required
                    inputProps={{
                        type: "email",
                        readOnly: !!invitation,
                        autoComplete: "email",
                        ...register("email", {
                            required: "Please enter your email",
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: "Please enter a valid email address"
                            },
                        }),
                    }}
                    showTips={dirtyFields.email}
                    error={errors.email?.message}
                    tips={{
                        placeholder: "E.g., john.doe@example.com",
                        success: "Great! Valid email provided"
                    }}
                />
                <Field
                    label="Password"
                    required
                    inputProps={{
                        type: "password",
                        autoComplete: "new-password",
                        ...register("password", {
                            required: "Please enter a strong password",
                            minLength: {
                                value: 8,
                                message: "Password must be at least 8 characters"
                            },
                            pattern: {
                                value: /^(?=.*\d)(?=.*[@$!%*?&#()^+=\-_])[A-Za-z\d@$!%*?&#()^+=\-_]{8,}$/,
                                message: "Password must contain a number and a symbol."
                            },
                        })
                    }}
                    showTips={dirtyFields.password}
                    error={errors.password?.message}
                    tips={{
                        placeholder: "At least 8 characters with a number & symbol",
                        success: "Great! Your password is secure",
                    }}
                />
                <Field
                    label="Confirm Password"
                    required
                    inputProps={{
                        type: "password",
                        autoComplete: "new-password",
                        onKeyUp: (e) => {
                            if (e.key === 'Enter') {
                                next();
                            }
                        },
                        ...register("confirmPassword", {
                            required: "Please confirm your password",
                            validate: (value: string, formValues) => value === formValues.password || "Passwords do not match"
                        }),
                    }}
                    showTips={dirtyFields.confirmPassword}
                    error={errors.confirmPassword?.message}
                    tips={{
                        placeholder: "Re-enter the same password to confirm",
                        success: "Passwords match",
                    }}
                />
                <div>
                    <PrimaryButton type="button" onClick={next}>Continue</PrimaryButton>
                </div>

                <div className="relative flex items-center justify-center w-full mt-2 mb-2">
                    <div className="absolute w-full h-px bg-gray-300"></div>
                    <div className="relative px-4 bg-white text-sm text-gray-500">Or</div>
                </div>

                <GoogleLoginButton
                    preSuccess={(token) => {
                        const { Email } = jwtDecode<JwtTokenPayload>(token);
                        if (invitation && invitation.email !== Email) {
                            setError('SSO Email does not match invitation');
                            return false;
                        }
                        return true;
                    }}
                    onError={(error) => console.error("Google authentication error:", error)}
                />
            </div>
            <p className="mt-8 text-center font-[600] text-[#1C1C1C]">
                Already registered? <NavLink to="/login" className="text-[#26818E] relative"><span className='relative z-20'>Sign In</span></NavLink>
            </p>
        </>
    )
}

const SignupStepTwo = ({form, back, isLoading, signUpError, token, invitation}: {
    form: UseFormReturn<SignupFormParams>;
    back: () => void;
    isLoading: boolean;
    signUpError: string | null;
    token: string | null;
    invitation?: { email: string; organizationName: string; organizationId: string; } | null;
}) => {
    const { register, formState: {dirtyFields, errors, isValid}, setValue, trigger, setFocus, setError, clearErrors, watch } = form;
    const onChangeCaptcha = (data: string | null) => {
        setValue('recaptchaToken', data ? data : undefined);
    };

    const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (!isValid) {
            e.preventDefault();
            trigger(['firstName', 'lastName', 'organizationName', 'promoCode', 'referralCode']);
            if (!watch('recaptchaToken')) {
                setError('recaptchaToken', { type: 'required', message: 'Please verify you are human' });
            } else {
                clearErrors('recaptchaToken');
            }
        }
    }

    useEffect(() => {
        if (token) {
            setFocus('organizationName');
        } else {
            setFocus('firstName');
        }
    }, [setFocus, token]);

    return (
        <div className="items-center bg-white">
            <button onClick={back} className='md:mt--5 mt-0 flex align-center items-center text-[#1C1C1C] cursor-pointer font-semibold text-sm'>
                <IoIosArrowBack className=' ml-[-6px] w-4 h-4 mt--0.5 mr-0.5' /> Back to Account information
            </button>
            <Card.Header title='Registration' subtitle='Contact Information' />
            <div className='flex flex-col gap-5'>
                <div className='flex flex-col md:flex-row gap-4'>
                    <Field
                        label="First Name"
                        required
                        showTips={dirtyFields.firstName}
                        error={errors.firstName?.message}
                        inputProps={{
                            autoComplete: "given-name",
                            ...register("firstName", {required: "Please enter your first name"})
                        }}
                        tips={{
                            placeholder: "E.g., John",
                        }}
                    />
                    <Field
                        label="Last Name"
                        required
                        showTips={dirtyFields.lastName}
                        error={errors.lastName?.message}
                        inputProps={{
                            autoComplete: "family-name",
                            ...register("lastName", {required: "Please enter your last name"})
                        }}
                        tips={{
                            placeholder: "E.g., Doe",
                        }}
                    />
                </div>
                <Field
                    label="Organization Name"
                    required
                    showTips={dirtyFields.organizationName}
                    error={errors.organizationName?.message}
                    inputProps={{
                        autoComplete: "organization",
                        readOnly: !!invitation,
                        ...register("organizationName", {required: "Please enter your organization name"})
                    }}
                />
                { !invitation &&
                    <Field
                        label="Promo Code"
                        showTips={dirtyFields.promoCode}
                        error={errors.promoCode?.message}
                        inputProps={{
                            autoComplete: "off",
                            ...register("promoCode")
                        }}
                    />
                }
                <Field
                    label="Referral Code"
                    showTips={dirtyFields.referralCode}
                    error={errors.referralCode?.message}
                    inputProps={{
                        autoComplete: "off",
                        ...register("referralCode")
                    }}
                />
                <Field
                    showTips={dirtyFields.recaptchaToken}
                    error={errors.recaptchaToken?.message}
                >
                    <ReCAPTCHA
                        sitekey={'6LfR7_spAAAAABN_ixAEvKNNOYgp8bl_KIBm-Izc'}
                        onChange={onChangeCaptcha}
                        className='w-full flex md:justify-start justify-center'
                    />
                </Field>
                {signUpError && <p className="text-red-500">{signUpError}</p>}
                <div>
                    <PrimaryButton type="submit" onClick={onClick} disabled={isLoading}>Sign Up</PrimaryButton>
                </div>
            </div>
        </div>
    )
}

const SignupSuccess = () => {
    return (
        <Card>
            <Card.Header title='Registration complete!' />
            <p className="text-[#1C1C1C] font-semibold">Registration successful! Please check your inbox to confirm your email.</p>
            <div className="flex flex-col md:flex-row mt-4 items-center md:gap-4 w-full ">
                <div className="w-full h-12 flex items-center justify-center">
                    Go to your email inbox.
                </div>
            </div>
        </Card>
    )
}

export default Signup;
